import React, { useState, useEffect, useRef } from "react";
import "./tableStyle.css";
import { reportsApi, exportYearlyReportExcel } from "../../Services/Services";
import { Col, Input, Button, Select, Form, message } from "antd";
import { useReactToPrint } from "react-to-print";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "../util";
import { Link } from "react-router-dom";

const { Option } = Select;

export default function YearlyReport({ onChange }) {
  const [orderList, setOrderList] = useState([]); // all loaded pages
  const [deliveryOrder, setDeliveryOrder] = useState([]); // filtered display
  const [form] = Form.useForm();
  const [exporting, setExporting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filters, setFilters] = useState({
    truck_owner_name: "",
    do_no: "",
    check_no: "",
    check_date: "",
    year: new Date().getFullYear(),
  });

  const limit = 50;
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });

  const fetchData = async (offset = 0, appliedFilters = filters) => {
    try {
      const response = await reportsApi({
        report_name: "yearly_report",
        com_name: localStorage.getItem("com_name"),
        limit,
        offset,
        ...appliedFilters,
      });

      const newData = Array.isArray(response)
        ? response
        : response.results
        ? response.results
        : [];

      setOrderList((prev) => {
        const updated = offset === 0 ? newData : [...prev, ...newData];
        setDeliveryOrder(updated);
        return updated;
      });

      if (response && typeof response.next !== "undefined") {
        setHasMore(!!response.next);
      } else {
        setHasMore(newData.length === limit);
      }
    } catch (err) {
      console.error("YearlyReport fetch error:", err);
      setHasMore(false);
    }
  };

  const debouncedFetchData = useRef(
    debounce((updatedFilters) => {
      fetchData(0, updatedFilters); // reset offset
    }, 500)
  ).current;

  useEffect(() => {
    setOrderList([]);
    setDeliveryOrder([]);
    setHasMore(true);
    fetchData(0);
  }, []);

  const fetchMoreData = () => {
    if (!hasMore) return;
    const offset = orderList.length;
    fetchData(offset);
  };

  const handleValuesChange = (changedValues, allValues) => {
    setFilters(allValues);
    debouncedFetchData(allValues); // use debounce
  };

  // export to excel uses deliveryOrder (visible rows)
  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      message.loading({ content: "Generating Excel file...", key: "export" });

      const params = {
        com_name: localStorage.getItem("com_name"),
        year: filters.year || selectedYear,
        truck_owner_name: filters.truck_owner_name || "",
        check_no: filters.check_no || "",
        check_date: filters.check_date || "",
        do_no: filters.do_no || "",
        user_type: localStorage.getItem("user_type"),
        lifter_id: localStorage.getItem("user_id"),
      };

      const response = await exportYearlyReportExcel(params);

      if (!response.success) {
        throw new Error(response.error || "Failed to export Excel file");
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      let filename = `YearlyFreightReport_${params.year}_${timestamp}.xlsx`;

      // Extract filename from headers if available
      const contentDisposition = response.headers?.["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({
        content: "Excel file downloaded successfully!",
        key: "export",
        duration: 2,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error({
        content:
          error.message || "Failed to export Excel file. Please try again.",
        key: "export",
        duration: 3,
      });
    } finally {
      setExporting(false);
    }
  };

  const formateDate = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  // years list
  const getYearsArray = (startYear, endYear) => {
    const arr = [];
    for (let y = startYear; y <= endYear; y++) arr.push(y);
    return arr;
  };
  const currentYear = new Date().getFullYear();
  const years = getYearsArray(2023, currentYear);

  return (
    <div>
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <div className=" grid grid-cols-1 sm:grid sm:grid-cols-6 sm:gap-2 m-auto px-5 sm:w-[70%]  md:w-[100%]">
          <Col>
            <label>
              <b>Select Year</b>
            </label>
            <br />
            <Form.Item name="year" initialValue={selectedYear}>
              <Select showSearch>
                {years.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col>
            <label>
              <b>Search By Do No.</b>
            </label>
            <br />
            <Form.Item name="do_no">
              <Input placeholder="Search by DO No." />
            </Form.Item>
          </Col>
          <Col>
            <label>
              <b>Truck Owner Name</b>
            </label>
            <br />
            <Form.Item name="truck_onwer_name">
              <Input placeholder="Search by Truck Onwer Name." />
            </Form.Item>
          </Col>

          <Col>
            <label>
              <b>Search By Check No</b>
            </label>
            <br />
            <Form.Item name="check_no">
              <Input placeholder="Search by Check No" />
            </Form.Item>
          </Col>
          <Col>
            <label>
              <b>Search By Check Date</b>
            </label>
            <br />
            <Form.Item name="check_date">
              <Input placeholder="Search by Check Date." type="date" />
            </Form.Item>
          </Col>
          <Col>
            <Button
              style={{
                bottom: "-10px",
                margin: "10px",
                backgroundColor: "black",
                color: "white",
                fontWeight: "600",
              }}
              onClick={handlePrint}
            >
              <FaFilePdf />
            </Button>
            <Button
              loading={exporting}
              disabled={exporting}
              style={{
                bottom: "-10px",
                margin: "10px",
                backgroundColor: "green",
                color: "white",
                fontWeight: "600",
              }}
              onClick={handleExportToExcel}
            >
              <FaFileExcel />
            </Button>
          </Col>
        </div>
      </Form>
      <div
        id="scrollableDiv"
        style={{ maxHeight: "500px", overflow: "auto" }}
        ref={componentRef}
      >
        <table id="my-table" className="w-full">
          <thead className="bg-[#151B54] w-full text-white">
            <tr>
              <th>Sr. No</th>
              <th> Id</th>
              <th>Do No</th>
              <th>Party Name</th>
              <th>Truck Owner Name</th>
              <th>Pan No</th>
              <th>Vehicle No</th>
              <th>Dipatch Qty</th>
              <th>Recieved Qty</th>
              <th>Freight Qty</th>
              <th>Sort Qty</th>
              <th>Moisture %</th>
              <th>Actual Qty</th>
              <th>Moisture Deduct Qty</th>
              <th>Moisture Amt</th>
              <th>Moisture Deduct</th>
              <th>Sort Amount</th>
              <th>Freight Rate</th>
              <th>Freight Amt</th>
              <th>Tds %</th>
              <th>Tds Amt</th>
              <th>Advance Amt</th>
              <th>Commision Amt</th>
              <th>Pay Amt</th>
              <th>Dispatch Date</th>
              <th>Received Date</th>
              <th>Check No</th>
              <th>Check Date</th>
              <th>Bank Name</th>
              <th>IFSC Code</th>
              <th>Account No </th>
              <th>Aadhar No</th>
            </tr>
          </thead>
          <tbody>
            {deliveryOrder.map((e, index) => (
              <tr key={e.id}>
                <td>{index + 1}</td>
                <td>{e.id}</td>
                <td>
                  <Link to={`/do-view/${e.do_id}`}>
                    <u style={{ fontWeight: "600", color: "blue" }}>
                      {e.do_no}
                    </u>
                  </Link>
                </td>
                <td>{e.client_name}</td>
                <td>{e.truck_owner_name}</td>
                <td>{e.pan_no}</td>
                <td>{e.vehicle_no}</td>
                <td>{e.dispatch_quantity.toFixed(3)}</td>
                <td>{e.received_quantity.toFixed(3)}</td>
                <td>{e.freight_quantity.toFixed(3)}</td>
                <td>{e.sort_quantity}</td>
                <td>{e.moisture_received_per}</td>
                <td>{e.actual_quantity}</td>
                <td>{e.moisture_deduct_quantity}</td>
                <td>{e.moisture_amount}</td>
                <td>{e.moisture_deduct}</td>
                <td>{e.sort_amount}</td>
                <td>{e.freight_rate}</td>
                <td>{e.freight_amount}</td>
                <td>{e.tds_per}</td>
                <td>{e.tds}</td>
                <td>{e.advance}</td>
                <td>{e.commision}</td>
                <td>{e.pay_amount}</td>
                <td>{formateDate(e.date)}</td>
                <td>{formateDate(e.received_date)}</td>
                <td>{e.check_no}</td>
                <td>{formateDate(e.check_date)}</td>
                <td>{e.bank_name}</td>
                <td>{e.ifsc_code}</td>
                <td>{e.account_no}</td>
                <td>{e.aadhar_no}</td>
              </tr>
            ))}
            <tr style={{ backgroundColor: "#e9e9e9" }}>
              <td
                className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                colSpan={6}
              >
                <b>Total</b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.dispatch_quantity,
                      0
                    )
                    .toFixed(3)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.received_quantity,
                      0
                    )
                    .toFixed(3)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.freight_quantity,
                      0
                    )
                    .toFixed(3)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>

              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>

              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.moisture_amount,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.moisture_deduct,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.sort_amount,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>

              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.freight_amount,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"></td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.tds,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.advance,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.commision,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                <b>
                  {deliveryOrder
                    .reduce(
                      (accumulator, currentValue) =>
                        accumulator + +currentValue.pay_amount,
                      0
                    )
                    .toFixed(2)}
                </b>
              </td>
              <td
                className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                colSpan={8}
              ></td>
            </tr>
          </tbody>
        </table>
        <InfiniteScroll
          dataLength={deliveryOrder.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            deliveryOrder.length > 0 && hasMore ? (
              <div style={{ textAlign: "center", padding: "10px" }}>
                Loading...
              </div>
            ) : null
          }
          scrollableTarget="scrollableDiv"
        />
        {/* </InfiniteScroll> */}
      </div>
    </div>
  );
}
